import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, Button, Avatar,
  Select, MenuItem, FormControl, InputLabel, CircularProgress,
  IconButton, Stack, InputAdornment
} from "@mui/material";
import Grid from "@mui/material/Grid2"
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import dayjs from "dayjs";
import { toast } from "react-toastify";
import instance from "../../Utils/api";
import Image from "../../Utils/getImage";
import Header from "../layouts/Header";
import "../styles/pages/account-page.scss";

const INITIAL_STATE = {
  mobile: "", name: "", dob: null, location: "",
  Gender: "", profile_pic: null, size: "", alternative_number: "", user_id: "",
};

const Account = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalData, setOriginalData] = useState(INITIAL_STATE);
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [selectedFile, setSelectedFile] = useState(null);

  // Fetch initial data
  const fetchCustomerData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await instance.get("Customer/getCustomerById");
      const userData = response.data.data;
      if (userData) {
        const fullData = { ...INITIAL_STATE, ...userData };
        setFormData(fullData);
        setOriginalData(fullData);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load user data.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomerData();
  }, [fetchCustomerData]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (newValue) => {
    setFormData((prev) => ({ ...prev, dob: newValue ? newValue.format("YYYY-MM-DD") : null }));
  };

  const handleImageUpload = (e) => {
    const file = e.target?.files[0];
    if (file) {
      setSelectedFile(file);
      setFormData((prev) => ({ ...prev, profile_pic: URL.createObjectURL(file) }));
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    setSelectedFile(null);
    setIsEditMode(false);
  };


  const handleToggleOrUpdate = async () => {
    if (!isEditMode) {
      setIsEditMode(true);
      return;
    }
    if (formData.alternative_number.trim() !== "" && !/^\d{10}$/.test(formData.alternative_number.trim())) {
      toast.error("Please enter a valid 10-digit alternative number");
      return;
    }

    setIsSubmitting(true);
    const submissionForm = new FormData();
    Object.keys(formData).forEach(key => {
      if (key !== 'profile_pic' && formData[key] !== null) {
        submissionForm.append(key, formData[key]);
      }
    });

    if (selectedFile) {
      submissionForm.append("profile_pic", selectedFile);
    }

    try {
      const response = await instance.put("Customer/updateProfile", submissionForm);
      if (response.data.success) {
        toast.success(response.data.message);
        setOriginalData(formData);
        setIsEditMode(false);
        setSelectedFile(null);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Update failed.");
    } finally {
      setIsSubmitting(false);
    }
  };


  if (isLoading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Header />
      <Box className="account-page">
        <Typography variant="h4" className="account-title">
          My Profile
        </Typography>

        <Grid container spacing={4} className="account-content">
          {/* Profile Picture Section */}
          <Grid item size={{ xs: 12, md: 4 }} className="profile-picture-section">
            <Box className="avatar-container">
              {!originalData.profile_pic ?
                <Avatar
                  src={formData.profile_pic || undefined}
                  className="profile-avatar"
                >
                  {/* Fallback to initials */}
                  {formData.name.charAt(0)}
                </Avatar> :
                <Image src={originalData.profile_pic} className="profile-avatar" width={140} height={140} />}
              {isEditMode && (
                <IconButton component="label" className="edit-avatar-btn">
                  <EditIcon />
                  <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                </IconButton>
              )}
            </Box>
            <Typography variant="h6">{formData.name}</Typography>
            <Typography variant="body2" color="text.secondary">{formData.mobile}</Typography>
          </Grid>

          {/* Form Fields Section */}
          <Grid item size={{ xs: 12, md: 8 }} className="profile-form-section">
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  size="small"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditMode} />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField fullWidth
                  label="Mobile Number"
                  size="small"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  disabled />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Date of Birth"
                    format="DD/MM/YYYY"
                    value={formData.dob ? dayjs(formData.dob) : null}
                    onChange={handleDateChange}
                    disabled={!isEditMode}
                    sx={{ width: '100%' }}
                    slotProps={{
                      textField: { size: "small" }
                    }} />
                </LocalizationProvider>
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gender</InputLabel>
                  <Select
                    label="Gender"
                    name="Gender"
                    value={formData.Gender}
                    onChange={handleChange}
                    disabled={!isEditMode}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  size="small"
                  value={formData.location}
                  onChange={handleChange}
                  disabled={!isEditMode}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LocationOnIcon /></InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Standard Size"
                  size="small"
                  name="size"
                  value={formData.size}
                  onChange={handleChange}
                  disabled={!isEditMode}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <TextField fullWidth
                  label="Alternative Number"
                  size="small"
                  name="alternative_number"
                  value={formData.alternative_number}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    if (value.length <= 10) {
                      setFormData((prev) => ({ ...prev, alternative_number: value }));
                    }
                  }}
                  inputProps={{ maxLength: 10 }}
                  disabled={!isEditMode}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} className="action-buttons">
          {isEditMode && (
            <Button variant="outlined" color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleToggleOrUpdate}
            disabled={isSubmitting}
            startIcon={!isEditMode && <EditIcon />}
            width={100}
            style={{ marginRight: "40px" }}
          >
            {isSubmitting ? "Saving..." : (isEditMode ? "Save Changes" : "Edit Profile")}
          </Button>
        </Stack>
      </Box>
    </>
  );
};

export default Account;
